import { useLanguage } from "../context/LanguageContext";
import { PageHero, QuoteBand, PageCta } from "../components/PageHero";
import { Reveal, MaskedLine } from "../components/motion";

const Matrix = ({ p }) => {
  const q = Object.fromEntries(p.quadrants.map((x) => [x.key, x]));
  const cell = (item, variant) => {
    const styles = {
      performance: "bg-deep hover:bg-ink",
      mist: "bg-[#F3F7F9] hover:bg-ink",
      light: "bg-white hover:bg-ink",
      mistLight: "bg-[#F3F7F9] hover:bg-ink",
    };
    const dark = variant === "performance";
    return (
      <div
        data-testid={`matrix-cell-${item.key}`}
        className={`group flex flex-col justify-center p-4 transition-colors duration-700 sm:p-7 lg:p-10 ${styles[variant]}`}
      >
        <h3
          className={`font-head text-[18px] leading-snug transition-colors duration-700 sm:text-xl lg:text-2xl ${
            dark ? "text-wave" : "text-ink group-hover:text-wave"
          }`}
        >
          {item.title}
        </h3>
        <p
          className={`mt-3 text-[13px] font-light leading-relaxed transition-colors duration-700 sm:text-sm ${
            dark ? "text-white/70" : "text-ink/70 group-hover:text-white/70"
          }`}
        >
          {item.text}
        </p>
      </div>
    );
  };
  return (
    <div data-testid="team-matrix" className="relative">
      <p className="mb-3 text-center text-[10px] font-medium uppercase tracking-label text-wave sm:mb-4 sm:text-[11px]">
        {p.axisY} ↑
      </p>
      <div className="relative grid grid-cols-2 gap-px overflow-hidden border border-ink/10 bg-ink/10">
        {cell(q.kuschel, "mist")}
        {cell(q.performance, "performance")}
        {cell(q.bezug, "light")}
        {cell(q.brutalo, "mistLight")}
      </div>
      <p className="mt-3 text-right text-[10px] font-medium uppercase tracking-label text-wave sm:mt-4 sm:text-[11px]">
        {p.axisX} →
      </p>
    </div>
  );
};

export default function Teams() {
  const { t } = useLanguage();
  const p = t.pages.teams;
  return (
    <main data-testid="teams-page">
      <PageHero testId="teams-hero" label={p.label} title={p.title} sub={p.sub} />

      <QuoteBand
        testId="teams-quote"
        line1={p.quote1}
        line2={p.quote2}
        sideText={p.quoteSide}
      />

      <section data-testid="teams-matrix-section" className="bg-paper">
        <div className="mx-auto max-w-[1600px] px-6 py-28 sm:px-12 sm:py-40 md:px-16">
          <div className="grid gap-16 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <Reveal y={14}>
                <p className="text-[13px] font-medium uppercase tracking-label text-wave">{p.matrixLabel}</p>
              </Reveal>
              <Reveal delay={0.15}>
                <h2 className="font-head mt-8 text-3xl leading-tight text-ink sm:text-5xl">{p.matrixTitle}</h2>
              </Reveal>
              <Reveal delay={0.3}>
                <p className="mt-8 text-base font-light leading-relaxed text-ink/70 sm:text-lg">{p.matrixIntro}</p>
              </Reveal>
            </div>
            <Reveal delay={0.2} className="lg:col-span-6 lg:col-start-7">
              <Matrix p={p} />
            </Reveal>
          </div>
        </div>
      </section>

      <section data-testid="teams-change" className="grain relative overflow-hidden bg-deep text-white">
        <div className="mx-auto max-w-[1600px] px-6 py-32 sm:px-12 sm:py-48 md:px-16">
          <h2 className="font-head max-w-4xl text-3xl leading-[1.2] sm:text-5xl">
            <MaskedLine>{p.change}</MaskedLine>
          </h2>
        </div>
      </section>

      <section data-testid="teams-topics" className="bg-white">
        <div className="mx-auto max-w-[1600px] px-6 py-28 sm:px-12 sm:py-40 md:px-16">
          <Reveal>
            <h2 className="font-head max-w-2xl text-3xl leading-tight text-ink sm:text-4xl">{p.topicsTitle}</h2>
          </Reveal>
          <div className="mt-14 grid gap-x-12 gap-y-6 sm:grid-cols-2 lg:grid-cols-3">
            {p.topics.map((topic, i) => (
              <Reveal key={topic} delay={i * 0.06} className="group flex items-baseline gap-4 border-b border-ink/10 pb-5 transition-colors duration-500 hover:border-wave/70">
                <span className="font-head text-base text-wave">0{i + 1}</span>
                <span className="text-base font-light text-ink/85">{topic}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <PageCta title={p.ctaTitle} text={p.ctaText} />
    </main>
  );
}
